import React from "react";
import { useTranslation } from "react-i18next";
import { Edit, Trash2, Repeat, CheckCircle2, XCircle, ChevronDown, ChevronUp, Plus } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

const CyclePatternCard = ({
  pattern,
  expandedPattern,
  setExpandedPattern,
  handleOpenEditModal,
  handleOpenDeleteDialog,
  handleAddWeekToPattern,
  handleDeleteWeekFromPattern,
  handleUpdateWeekInPattern,
  schedules,
}) => {
  const { t } = useTranslation();
  const isExpanded = expandedPattern === pattern.id;

  return (
    <Card className="overflow-hidden">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <CardTitle className="flex items-center gap-2">
              <Repeat className="h-5 w-5 text-blue-500" />
              {pattern.name}
              <Badge variant="outline" className="ml-2">
                {t("planning.cycles.weekCount", { count: pattern.cycle_duration_weeks })}
              </Badge>
              {pattern.is_active ? (
                <CheckCircle2 className="h-4 w-4 text-green-500" />
              ) : (
                <XCircle className="h-4 w-4 text-gray-400" />
              )}
            </CardTitle>
            {pattern.description && (
              <CardDescription className="mt-1">{pattern.description}</CardDescription>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setExpandedPattern(isExpanded ? null : pattern.id)}
            >
              {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => handleOpenEditModal(pattern)}>
              <Edit className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleOpenDeleteDialog(pattern)}
              className="text-red-400 hover:text-red-300"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>

      {isExpanded && (
        <CardContent className="pt-0">
          <div className="border-t pt-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-medium">{t("planning.cycles.weeks")}</h4>
              {(pattern.cycle_weeks?.length || 0) < pattern.cycle_duration_weeks && (
                <Button variant="outline" size="sm" onClick={() => handleAddWeekToPattern(pattern.id)}>
                  <Plus className="h-3 w-3 mr-1" /> {t("planning.cycles.addWeek")}
                </Button>
              )}
            </div>

            {!pattern.cycle_weeks || pattern.cycle_weeks.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">{t("planning.cycles.emptyWeeks")}</p>
            ) : (
              <div className="space-y-2">
                {pattern.cycle_weeks
                  .slice()
                  .sort((a, b) => a.week_number - b.week_number)
                  .map((week) => (
                    <div key={week.id} className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                      <Badge variant="outline" className="shrink-0">S{week.week_number}</Badge>
                      <div className="flex-1">
                        <Select
                          value={week.schedule_id?.toString()}
                          onValueChange={(value) => handleUpdateWeekInPattern(week.id, parseInt(value))}
                        >
                          <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {schedules.map((s) => (
                              <SelectItem key={s.id} value={s.id.toString()}>{s.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteWeekFromPattern(week.id)}
                        className="text-red-400 hover:text-red-300"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
              </div>
            )}
          </div>
        </CardContent>
      )}
    </Card>
  );
};

export default CyclePatternCard;
